/**
 * Create / edit form for one Interview Question Bank question (Phase 4.2).
 *
 * Same form for both flows: a null `question` prop = create (POST), a present one
 * = edit (PATCH). Client-side validation mirrors the backend's ported rules
 * (interviewBank.js / generate_interview_seed.js) — non-empty text, ≥1 canonical
 * dimension, and a required consent_preamble whenever sensitivity is
 * charter_sensitive — so the form only submits payloads the backend will accept;
 * the backend remains the source of truth and re-validates.
 *
 * module is immutable on edit (the backend ignores it on PATCH), so the module
 * select is disabled when editing an existing question.
 *
 * Ported from quill-app/quill-admin's QuestionEditDialog.tsx. Redux
 * (createQuestion/updateQuestion thunks + s.interview.saving) replaced with
 * local state calling quillApi.interview.* directly.
 *
 * Dialog-shell choice: kept this component's own MUI Dialog structure rather
 * than rebasing onto AdminEditDialogShell. The shell's header (single-letter
 * accent avatar + top-of-dialog Active toggle + bottom dirty-bar/reset footer)
 * is built for flatter key/value editors; this form has a mid-form Active
 * switch, a dynamic follow-up-probe array editor, and a validation Alert that
 * needs to sit inline above the fields — moving all of that to fit the shell's
 * slots would be a bigger restructuring than "drops in with low friction"
 * allows for, so the theme-token-only Dialog below is the leaner match. Every
 * mutation still requires production-mutation confirmation — via the shared
 * components/quill/common/QuillActionConfirm (see that file for why it isn't
 * the shared components/help/ActionConfirm).
 */

import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Box,
  Stack,
  Typography,
  Select,
  OutlinedInput,
  Chip,
  InputLabel,
  FormControl,
  FormControlLabel,
  Switch,
  Slider,
  IconButton,
  Alert,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  quillApi,
  InterviewQuestion,
  QuestionInput,
  QuillApiError,
} from '../../../services/quillAdminService';
import QuillActionConfirm from '../common/QuillActionConfirm';
import {
  DIMENSIONS,
  MODULES,
  SENSITIVITIES,
  EXPECTED_DEPTHS,
  DIMENSION_LABEL,
  humanizeModule,
} from './interviewConstants';

interface Props {
  open: boolean;
  question: InterviewQuestion | null; // null = create
  onClose: () => void;
  onSaved?: (q: InterviewQuestion) => void;
}

interface FormState {
  module: string;
  question_text: string;
  dimension_keys: string[];
  priority: number;
  sensitivity: InterviewQuestion['sensitivity'];
  expected_depth: InterviewQuestion['expected_depth'];
  follow_ups: string[];
  consent_preamble: string;
  is_active: boolean;
}

function emptyForm(): FormState {
  return {
    module: MODULES[0],
    question_text: '',
    dimension_keys: [],
    priority: 0.5,
    sensitivity: 'none',
    expected_depth: 'story',
    follow_ups: [],
    consent_preamble: '',
    is_active: true,
  };
}

function fromQuestion(q: InterviewQuestion): FormState {
  return {
    module: q.module,
    question_text: q.question_text,
    dimension_keys: q.dimension_keys,
    priority: q.priority ?? 0.5,
    sensitivity: q.sensitivity,
    expected_depth: q.expected_depth,
    follow_ups: q.follow_ups,
    consent_preamble: q.consent_preamble ?? '',
    is_active: q.is_active,
  };
}

const QuestionEditDialog: React.FC<Props> = ({ open, question, onClose, onSaved }) => {
  const isEdit = !!question;

  const [form, setForm] = React.useState<FormState>(emptyForm());
  const [localError, setLocalError] = React.useState<string | null>(null);
  const [saving, setSaving] = React.useState(false);
  const [confirmOpen, setConfirmOpen] = React.useState(false);

  React.useEffect(() => {
    if (open) {
      setForm(question ? fromQuestion(question) : emptyForm());
      setLocalError(null);
      setConfirmOpen(false);
    }
  }, [open, question]);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const validate = (): string | null => {
    if (!form.question_text.trim()) return 'Question text is required.';
    if (form.dimension_keys.length === 0) return 'Pick at least one dimension.';
    if (form.sensitivity === 'charter_sensitive' && !form.consent_preamble.trim()) {
      return 'Charter-sensitive questions require a consent preamble.';
    }
    return null;
  };

  const requestSave = () => {
    const err = validate();
    if (err) {
      setLocalError(err);
      return;
    }
    setLocalError(null);
    setConfirmOpen(true);
  };

  // "Archive" is really the same PATCH as any other edit — is_active just
  // flips to false — but it gets its own confirmation copy since it's the one
  // path that takes a question out of active circulation for the interview
  // engine, and the operator should see that framing before confirming.
  const isArchiving = isEdit && !!question!.is_active && !form.is_active;
  const confirmCopy = !isEdit
    ? {
        title: 'Create this question?',
        message: `This creates a new interview question (${humanizeModule(form.module)}) in the production Quill database. It becomes visible to the interview engine immediately.`,
        confirmLabel: 'Create question',
      }
    : isArchiving
    ? {
        title: 'Archive this question?',
        message:
          'This deactivates the question in the production Quill database. It stops being offered by the interview engine but is not deleted — re-activate it any time by editing it again.',
        confirmLabel: 'Archive',
        emphasize: true,
      }
    : {
        title: 'Save changes?',
        message: 'This saves changes to this interview question in the production Quill database, effective immediately for the interview engine.',
        confirmLabel: 'Save changes',
      };

  const handleConfirm = async () => {
    setSaving(true);
    setLocalError(null);
    const body: QuestionInput = {
      question_text: form.question_text.trim(),
      dimension_keys: form.dimension_keys,
      priority: form.priority,
      sensitivity: form.sensitivity,
      expected_depth: form.expected_depth,
      follow_ups: form.follow_ups.map((f) => f.trim()).filter((f) => f !== ''),
      consent_preamble: form.consent_preamble.trim() ? form.consent_preamble.trim() : null,
      is_active: form.is_active,
    };

    try {
      const saved = isEdit
        ? await quillApi.interview.updateQuestion(question!.question_id, body)
        : await quillApi.interview.createQuestion({ ...body, module: form.module });
      setSaving(false);
      setConfirmOpen(false);
      onSaved?.(saved);
      onClose();
    } catch (e) {
      setSaving(false);
      setConfirmOpen(false);
      setLocalError(e instanceof QuillApiError ? e.message : 'Save failed. Check the fields and try again.');
    }
  };

  const showConsent = form.sensitivity === 'charter_sensitive';

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>{isEdit ? 'Edit question' : 'New question'}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2.5} sx={{ mt: 1 }}>
            {localError && <Alert severity="error">{localError}</Alert>}

            <TextField
              select
              label="Module"
              value={form.module}
              onChange={(e) => set('module', e.target.value)}
              disabled={isEdit}
              helperText={isEdit ? 'Module is fixed once a question exists.' : undefined}
              fullWidth
            >
              {MODULES.map((m) => (
                <MenuItem key={m} value={m}>
                  {humanizeModule(m)}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              label="Question text"
              value={form.question_text}
              onChange={(e) => set('question_text', e.target.value)}
              multiline
              minRows={2}
              fullWidth
              required
            />

            <FormControl fullWidth>
              <InputLabel id="dimensions-label">Dimensions</InputLabel>
              <Select
                labelId="dimensions-label"
                multiple
                value={form.dimension_keys}
                onChange={(e) =>
                  set('dimension_keys', typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)
                }
                input={<OutlinedInput label="Dimensions" />}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {(selected as string[]).map((key) => (
                      <Chip key={key} size="small" label={DIMENSION_LABEL[key] || key} />
                    ))}
                  </Box>
                )}
              >
                {DIMENSIONS.map((d) => (
                  <MenuItem key={d.key} value={d.key}>
                    {d.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                select
                label="Sensitivity"
                value={form.sensitivity}
                onChange={(e) => set('sensitivity', e.target.value as FormState['sensitivity'])}
                fullWidth
              >
                {SENSITIVITIES.map((s) => (
                  <MenuItem key={s} value={s}>
                    {s}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                label="Expected depth"
                value={form.expected_depth}
                onChange={(e) => set('expected_depth', e.target.value as FormState['expected_depth'])}
                fullWidth
              >
                {EXPECTED_DEPTHS.map((d) => (
                  <MenuItem key={d} value={d}>
                    {d}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>

            <Box>
              <Typography variant="body2" gutterBottom>
                Priority: <strong>{form.priority.toFixed(2)}</strong>
              </Typography>
              <Slider
                value={form.priority}
                onChange={(_e, v) => set('priority', Array.isArray(v) ? v[0] : v)}
                min={0}
                max={1}
                step={0.05}
                valueLabelDisplay="auto"
                aria-label="priority"
              />
            </Box>

            {showConsent && (
              <TextField
                label="Consent preamble (required for charter-sensitive)"
                value={form.consent_preamble}
                onChange={(e) => set('consent_preamble', e.target.value)}
                multiline
                minRows={2}
                fullWidth
                required
                error={showConsent && !form.consent_preamble.trim()}
              />
            )}

            <Box>
              <Typography variant="body2" gutterBottom>
                Follow-up probes
              </Typography>
              <Stack spacing={1}>
                {form.follow_ups.map((f, i) => (
                  <Stack key={i} direction="row" spacing={1} alignItems="center">
                    <TextField
                      value={f}
                      onChange={(e) =>
                        set(
                          'follow_ups',
                          form.follow_ups.map((x, j) => (j === i ? e.target.value : x))
                        )
                      }
                      size="small"
                      fullWidth
                      placeholder={`Follow-up ${i + 1}`}
                      inputProps={{ 'aria-label': `follow-up ${i + 1}` }}
                    />
                    <IconButton
                      aria-label={`remove follow-up ${i + 1}`}
                      onClick={() => set('follow_ups', form.follow_ups.filter((_x, j) => j !== i))}
                    >
                      <DeleteOutlineIcon />
                    </IconButton>
                  </Stack>
                ))}
                <Button
                  startIcon={<AddIcon />}
                  size="small"
                  onClick={() => set('follow_ups', [...form.follow_ups, ''])}
                  sx={{ alignSelf: 'flex-start' }}
                >
                  Add follow-up
                </Button>
              </Stack>
            </Box>

            <FormControlLabel
              control={<Switch checked={form.is_active} onChange={(e) => set('is_active', e.target.checked)} />}
              label={form.is_active ? 'Active' : 'Inactive (soft-deleted)'}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={requestSave} disabled={saving}>
            {isEdit ? 'Save changes' : 'Create question'}
          </Button>
        </DialogActions>
      </Dialog>

      <QuillActionConfirm
        open={confirmOpen}
        title={confirmCopy.title}
        message={confirmCopy.message}
        confirmLabel={confirmCopy.confirmLabel}
        destructive={'emphasize' in confirmCopy ? confirmCopy.emphasize : undefined}
        loading={saving}
        pendingLabel="Applying…"
        onConfirm={handleConfirm}
        onCancel={() => setConfirmOpen(false)}
      />
    </>
  );
};

export default QuestionEditDialog;
